import { useEffect, useState } from "react";
import { OutputFormat, RequestType, geocode } from "react-geocode";

import { FARM_LOCATION_CACHE_PREFIX, getGoogleKey } from "utils";

import { COUNTRY_CODE } from "consts";

import { Coordinates } from "models";

export type FarmLocationStatus = "idle" | "loading" | "ready" | "unavailable";

export type FarmLocation = { status: FarmLocationStatus; location: Coordinates | null };

const GERMANY_CENTRE: Coordinates = { lat: 51.1657, lng: 10.4515 };

const COUNTRY_CENTRES: Partial<Record<COUNTRY_CODE, Coordinates>> = {
  [COUNTRY_CODE.GERMANY]: GERMANY_CENTRE,
  [COUNTRY_CODE.AUSTRIA]: { lat: 47.5162, lng: 14.5501 },
  [COUNTRY_CODE.SWITZERLAND]: { lat: 46.8182, lng: 8.2275 },
  [COUNTRY_CODE.POLAND]: { lat: 51.9194, lng: 19.1451 },
  [COUNTRY_CODE.CZECHIA]: { lat: 49.8175, lng: 15.473 },
  [COUNTRY_CODE.SLOVAKIA]: { lat: 48.669, lng: 19.699 },
  [COUNTRY_CODE.HUNGARY]: { lat: 47.1625, lng: 19.5033 },
  [COUNTRY_CODE.UNITED_KINGDOM]: { lat: 55.3781, lng: -3.436 },
  [COUNTRY_CODE.USA]: { lat: 37.0902, lng: -95.7129 },
};

export const countryCentre = (country: COUNTRY_CODE | null | undefined): Coordinates => (country && COUNTRY_CENTRES[country]) || GERMANY_CENTRE;

type CachedFarmLocation = { address: string; location: Coordinates };

const readCache = (farmId: string, address: string): Coordinates | null => {
  try {
    const cached = JSON.parse(localStorage.getItem(`${FARM_LOCATION_CACHE_PREFIX}${farmId}`) ?? "null") as CachedFarmLocation | null;
    return cached?.address === address ? cached.location : null;
  } catch {
    return null;
  }
};

const writeCache = (farmId: string, address: string, location: Coordinates) => {
  try {
    localStorage.setItem(`${FARM_LOCATION_CACHE_PREFIX}${farmId}`, JSON.stringify({ address, location } satisfies CachedFarmLocation));
  } catch {
    // storage full or blocked: the next visit geocodes again
  }
};

const geocodeAddress = async (address: string, country: COUNTRY_CODE): Promise<Coordinates | null> => {
  const key = getGoogleKey();
  if (!key) return null;

  const response = await geocode(RequestType.ADDRESS, address, {
    key,
    outputFormat: OutputFormat.JSON,
    components: `country:${country}`,
  });
  const location = response?.results?.[0]?.geometry?.location;
  return typeof location?.lat === "number" && typeof location?.lng === "number" ? { lat: location.lat, lng: location.lng } : null;
};

type UseFarmLocationArgs = {
  farmId: string | undefined;
  zip: string | null | undefined;
  city: string | null | undefined;
  /** restricts the lookup; unknown (the farm details failed): no lookup, a bare zip could land in another country */
  country: COUNTRY_CODE | null | undefined;
  /** only the no-fields map needs the point, so the request runs only then */
  enabled: boolean;
};

/** The farm's approximate position from its zip and city (there is no stored coordinate), cached per farm until the address changes. */
export const useFarmLocation = ({ farmId, zip, city, country, enabled }: UseFarmLocationArgs): FarmLocation => {
  const [farmLocation, setFarmLocation] = useState<FarmLocation>({ status: "idle", location: null });
  const address = [zip, city].filter(Boolean).join(" ");

  useEffect(() => {
    if (!enabled || !farmId) {
      setFarmLocation({ status: "idle", location: null });
      return undefined;
    }
    if (!address || !country) {
      setFarmLocation({ status: "unavailable", location: null });
      return undefined;
    }

    const cached = readCache(farmId, address);
    if (cached) {
      setFarmLocation({ status: "ready", location: cached });
      return undefined;
    }

    let cancelled = false;
    setFarmLocation({ status: "loading", location: null });
    geocodeAddress(address, country)
      .then((location) => {
        if (cancelled) return;
        if (location) writeCache(farmId, address, location);
        setFarmLocation(location ? { status: "ready", location } : { status: "unavailable", location: null });
      })
      .catch(() => {
        if (!cancelled) setFarmLocation({ status: "unavailable", location: null });
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, farmId, address, country]);

  return farmLocation;
};
